import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { MenuItem } from "@mui/material";
import { C, fmtINR } from "../constants";
import { IPD_COLORS } from "../../ipd/utils/ipdColors";
import IpdTextField from "../../ipd/utils/IpdTextField";
import IpdFormField from "../../ipd/utils/IpdFormField";
import IpdButton from "../../ipd/utils/IpdButton";
import SvgIcon from "../SvgIcon";
import {
  listPackages,
  getPackage,
  listPackageTariffs,
  assignPackage,
  describeAssignError,
} from "../package-billing-api";

// Shared modal-shell shape used across ipd-billing — hand-rolled per file, not a common import.
function ModalShell({ title, subtitle, onClose, children, footer, width = 460 }) {
  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(13,35,63,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "20px" }}
      onClick={onClose}
    >
      {/* boxShadow rgba(13,35,63,0.28) has no IPD_COLORS.shadow equivalent — left as-is, matches CashCounterUI.js precedent */}
      <div
        style={{ background: IPD_COLORS.cardBg, borderRadius: "16px", padding: "28px", width: `${width}px`, maxWidth: "92vw", boxShadow: "0 24px 64px rgba(13,35,63,0.28)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: "0 0 6px", fontSize: "17px", fontWeight: "800", color: C.text }}>{title}</h3>
        {subtitle && <p style={{ margin: "0 0 18px", fontSize: "12px", color: C.textSub }}>{subtitle}</p>}
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>{children}</div>
        <div style={{ display: "flex", gap: "10px", marginTop: "22px" }}>{footer}</div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Package assign modal (Module 4) — pick an ACTIVE package, preview the tariff
// that would apply, confirm, and POST /ipd_admission/{id}/billing/package-assign/.
// The preview is scoped by payer type first and bed type second: a self-pay
// admission only sees payer-less tariffs, any other payer only sees tariffs of
// their own payer type belonging to them, else the payer-less tariff. It stays an
// estimate: the server may resolve a different price via a corporate rate-card
// PACKAGE_CODE override, which isn't visible client-side.
// ─────────────────────────────────────────────────────────────────────────────

// Tariffs are bed-type scoped: `bedType` set means the row applies to that bed
// type only, `bedType` null means it covers every bed. An exact bed-type match
// wins over an all-beds row; a row for a different bed type is never shown.
// With no bed type on the admission context (id-only restore) there is nothing
// to scope by, so the first candidate stands as before.
function pickForBedType(candidates, bedTypeCode) {
  if (!bedTypeCode) return candidates[0] || null;
  return (
    candidates.find((t) => t.bedType === bedTypeCode) ||
    candidates.find((t) => !t.bedType) ||
    null
  );
}

// billing_setup's payer_type spelling for an admission with no payer of its own.
const SELF_PAY_TYPES = ["SELF_PAY", "SELF", "CASH"];

const TARIFF_TYPE_LABEL = {
  HOSPITAL: "Hospital",
  CORPORATE: "Corporate",
  TPA: "TPA",
  INSURANCE: "Insurance",
  GOVERNMENT: "Government",
};

// Payer gate, applied before the bed-type scoping: a self-pay admission may only
// use a tariff that carries no payer (HOSPITAL is the only type the backend lets
// you save without one). Anyone else must match the tariff type to their own payer
// type AND be that payer's own row — a CORPORATE admission never prices off a
// TPA/INSURANCE tariff, nor off another corporate's contract. Matching on bed type
// alone is what let a cash admission preview another corporate's package price.
// A payer with no tariff of their own falls back to the payer-less tariff, which
// is what the server does.
function pickTariff(active, payerId, payerType, bedTypeCode) {
  const type = String(payerType || "").trim().toUpperCase();
  if (type && !SELF_PAY_TYPES.includes(type) && payerId) {
    const row = pickForBedType(
      active.filter((t) => t.tariffType === type && t.payerId === payerId),
      bedTypeCode
    );
    if (row) return row;
  }
  return pickForBedType(active.filter((t) => !t.payerId), bedTypeCode);
}

export default function PackageAssignModal({ admissionId, payerId, payerType, bedTypeCode, onClose, onAssigned }) {
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [selectedPackageId, setSelectedPackageId] = useState("");
  const [versionId, setVersionId] = useState(null);
  const [pricePreview, setPricePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      setLoadingPackages(true);
      try {
        setPackages(await listPackages({ status: "ACTIVE", activeOnly: true }));
      } catch (err) {
        setError(describeAssignError(err));
        setPackages([]);
      } finally {
        setLoadingPackages(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedPackageId) { setPricePreview(null); setVersionId(null); return; }
    let cancelled = false;
    setPreviewLoading(true);
    (async () => {
      try {
        const { versions } = await getPackage(selectedPackageId);
        const published = versions.find((v) => v.status === "PUBLISHED") || versions[0] || null;
        if (!published) { if (!cancelled) { setPricePreview(null); setVersionId(null); } return; }
        const tariffs = await listPackageTariffs(published.id);
        const active = tariffs.filter((t) => t.active);
        if (!cancelled) {
          setVersionId(published.id);
          setPricePreview(pickTariff(active, payerId, payerType, bedTypeCode));
        }
      } catch {
        if (!cancelled) { setPricePreview(null); setVersionId(null); }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedPackageId, payerId, payerType, bedTypeCode]);

  const selectedPackage = packages.find((p) => p.id === selectedPackageId) || null;

  const submit = async () => {
    if (!selectedPackageId) { setError("Select a package to assign."); return; }
    setSaving(true);
    setError("");
    try {
      const res = await assignPackage(admissionId, {
        package_id: selectedPackageId,
        package_version_id: versionId || undefined,
        remarks: remarks.trim() || undefined,
        create_initial_visit: true,
      });
      toast.success(
        res.appliedPackagePrice != null
          ? `${res.packageCode} assigned — ${fmtINR(res.appliedPackagePrice)}`
          : `${res.packageCode} assigned`
      );
      onAssigned(res);
    } catch (err) {
      setError(describeAssignError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title="Assign Package"
      subtitle="Freezes a snapshot of the package's tariff and entitlements for this admission."
      onClose={onClose}
      footer={<>
        <IpdButton variant="outlined" onClick={onClose} disabled={saving}>Cancel</IpdButton>
        <IpdButton onClick={submit} disabled={!selectedPackageId} loading={saving}>
          Assign Package
        </IpdButton>
      </>}
    >
      <IpdFormField label="Package">
        <IpdTextField
          select
          fullWidth
          value={selectedPackageId}
          onChange={(e) => { setSelectedPackageId(e.target.value); setError(""); }}
          disabled={loadingPackages}
        >
          <MenuItem value="">{loadingPackages ? "Loading packages…" : "Select a package…"}</MenuItem>
          {packages.map((p) => (
            <MenuItem key={p.id} value={p.id}>{p.code} — {p.name}</MenuItem>
          ))}
        </IpdTextField>
      </IpdFormField>
      {!loadingPackages && packages.length === 0 && (
        <div style={{ fontSize: "11px", color: C.textSub, marginTop: "5px" }}>No active published packages found.</div>
      )}

      {selectedPackage && (
        <div style={{ padding: "12px 14px", background: IPD_COLORS.pageBg, border: `1px solid ${C.border}`, borderRadius: "8px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "12px", fontWeight: "700", color: C.textSub }}>
              {TARIFF_TYPE_LABEL[pricePreview?.tariffType] || "Hospital"} tariff (estimate)
            </span>
            {previewLoading ? (
              <span style={{ fontSize: "12px", color: C.textSub }}>Loading…</span>
            ) : pricePreview ? (
              <span style={{ fontSize: "17px", fontWeight: "800", color: C.text }}>{fmtINR(pricePreview.packagePrice)}</span>
            ) : (
              <span style={{ fontSize: "12px", color: C.danger }}>
                {bedTypeCode
                  ? `No tariff for this payer · bed type ${bedTypeCode}`
                  : "No published tariff for this payer"}
              </span>
            )}
          </div>
          {pricePreview && (
            <div style={{ fontSize: "11px", color: C.textSub, marginTop: "3px" }}>
              Bed type {pricePreview.bedType || "All beds"}
              {pricePreview.payerName ? ` · ${pricePreview.payerName}` : ""}
            </div>
          )}
          {pricePreview?.standardPackagePrice != null && pricePreview.standardPackagePrice !== pricePreview.packagePrice && (
            <div style={{ fontSize: "11px", color: C.textSub, marginTop: "3px" }}>
              Standard price {fmtINR(pricePreview.standardPackagePrice)} — final price is confirmed after assignment (may include a corporate rate-card override).
            </div>
          )}
        </div>
      )}

      <IpdFormField label="Remarks (optional)">
        <IpdTextField fullWidth multiline rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Note for this assignment" />
      </IpdFormField>

      {error && (
        <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: C.danger, fontWeight: "600" }}>
          <SvgIcon name="alert-triangle" size={13} color={C.danger} />
          {error}
        </div>
      )}
    </ModalShell>
  );
}
